import { useState } from "react";
import { Spinner } from "@phosphor-icons/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";

import { updateCourse } from "@/api";
import { useCourses, useMaterials, useMedia } from "@/hooks";
import type { AddEditCourse, Course } from "@/types";
import {
  Button,
  ControlledDropzone,
  errorToast,
  Input,
  MultiSelect,
  Tabs,
  TextArea,
} from "@/ui";
import type { Tab } from "@/ui";
import { convertToOptions } from "@/utils/selectOptions";

interface EditCourseFormProps {
  course: Course;
  onClose: () => void;
}

interface FormValues {
  name: string;
  description: string;
  image?: File;
  banner?: File;
}

export const EditCourseForm = ({
  course: initialCourse,
  onClose,
}: EditCourseFormProps) => {
  const { useUpload } = useMedia();
  const { upload, isUploading } = useUpload();
  const queryClient = useQueryClient();

  const { useCourse } = useCourses();
  const { data: courseData } = useCourse(initialCourse.id);
  const course = courseData ?? initialCourse;

  const { useAllMaterials } = useMaterials();
  const { data: materialsData } = useAllMaterials();
  const materialOptions = convertToOptions(materialsData?.data ?? []);

  const [selectedMaterials, setSelectedMaterials] = useState<string[]>(
    course.materials?.map((m) => m.id) ?? [],
  );

  const { mutateAsync: updateCourseMutation, isPending } = useMutation({
    mutationFn: updateCourse,
  });

  const {
    formState: { errors, isValid },
    handleSubmit,
    register,
    control,
  } = useForm<FormValues>({
    mode: "all",
    defaultValues: {
      name: course.name,
      description: course.description,
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      const courseData: AddEditCourse = {
        name: data.name,
        description: data.description,
        partner_id: course.partner.id,
        image: course.image,
        banner: course.banner,
        image_extension: data.image
          ? data.image.name.split(".").pop()
          : undefined,
        banner_extension: data.banner
          ? data.banner.name.split(".").pop()
          : undefined,
        material_ids: selectedMaterials,
        related_resources:
          course.relatedResources?.map((r) => ({
            name: r.name,
            duration: r.duration,
            url: r.url,
          })) || [],
        additional_resources: course.additionalResources || [],
      };

      await updateCourseMutation({
        courseId: course.id,
        course: courseData,
      });

      if (data.image) {
        const result = await upload(
          data.image,
          `/courses/${course.id}/image/upload-url`,
        );
        if (!result.success) {
          errorToast("Failed to upload image. Please try again.");
          return;
        }
      }

      if (data.banner) {
        const result = await upload(
          data.banner,
          `/courses/${course.id}/banner/upload-url`,
        );
        if (!result.success) {
          errorToast("Failed to upload banner. Please try again.");
          return;
        }
      }

      await queryClient.invalidateQueries({
        queryKey: ["courses"],
      });
      onClose();
    } catch (error) {
      errorToast(error);
    }
  };

  const tabs: Tab[] = [
    {
      label: "Details",
      roles: ["admin", "partner"],
      component: (
        <div className="flex flex-col gap-y-8 py-4">
          <Input
            id="name"
            label="Course Name"
            placeholder="e.g., Mindfulness for Life"
            {...register("name", { required: "Course name is required" })}
            error={errors.name?.message}
            required
            className="text-neutral-900"
          />

          <TextArea
            id="description"
            label="Description"
            placeholder="Course description..."
            {...register("description", {
              required: "Description is required",
            })}
            error={errors.description?.message}
            required
            className="text-neutral-900"
          />

          <ControlledDropzone
            name="image"
            control={control}
            label="Replace Image (Optional)"
            placeholder="Upload new course image"
            accept={{ "image/*": [".png", ".jpg", ".jpeg"] }}
          />

          <ControlledDropzone
            name="banner"
            control={control}
            label="Replace Banner (Optional)"
            placeholder="Upload new course banner"
            accept={{ "image/*": [".png", ".jpg", ".jpeg"] }}
          />
        </div>
      ),
    },
    {
      label: "Materials",
      roles: ["admin", "partner"],
      component: (
        <div className="flex flex-col gap-y-4 py-4">
          <MultiSelect
            label="Assigned Materials"
            placeholder="Select materials..."
            options={materialOptions}
            selectedOptions={selectedMaterials}
            onChange={setSelectedMaterials}
          />
        </div>
      ),
    },
    {
      label: "Structure",
      roles: ["admin", "partner"],
      component: (
        <div className="flex flex-col gap-y-4 py-4">
          <div className="flex flex-col gap-y-2">
            {course.sessions?.length === 0 && (
              <p className="italic text-neutral-500">No sessions defined.</p>
            )}
            {course.sessions?.map((session, index) => (
              <div
                key={session.id}
                className="rounded-lg border bg-neutral-50 p-3"
              >
                <h4 className="text-sm font-semibold">
                  Session {index + 1}: {session.name}
                </h4>
              </div>
            ))}
          </div>
        </div>
      ),
    },
  ];

  const [currentTabLabel, setCurrentTabLabel] = useState(tabs[0]?.label ?? "");

  return (
    <div className="flex flex-col gap-y-6">
      <Tabs
        tabs={tabs}
        userRole="admin"
        currentLabel={currentTabLabel}
        setCurrentLabel={setCurrentTabLabel}
        containerClassName="w-full"
      />

      <div className="flex justify-end gap-3 border-t border-neutral-200 pt-4">
        <Button onClick={onClose} variant="outlined" className="flex-grow">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          className="flex-grow"
          disabled={!isValid || isPending || isUploading}
        >
          {isPending || isUploading ? (
            <Spinner className="h-5 w-5 animate-spin" />
          ) : (
            "Save Changes"
          )}
        </Button>
      </div>
    </div>
  );
};
